import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UserDomainModel } from '../../../../../domainModels/user-domain-model';
import { ToastService } from '../../../../../services/toast-service';
import { LoadingOptions } from '../../../../../models/shared/loading-options';
import { AuthCodeRequest } from '../../../../../models/users/requests/auth-code-request';
import { UserChallengeName } from '../../../../../models/users/enum/user-challenge.name';
import { UserChallengeParamName } from '../../../../../models/users/enum/user-challenge-param-name';
import { PortalType } from '../../../../../models/enum/shared/portal-type';
import { NumberUtils } from '../../../../../utils/number-utils';

@Injectable()
export class VerifyMfaViewModel {
  private userDomainModel = inject(UserDomainModel);
  private router = inject(Router);
  private toastService = inject(ToastService);

  private readonly loadingMessage = $localize`Signing in`;

  private _loadingOpts = new BehaviorSubject<LoadingOptions>(LoadingOptions.default());
  public loadingOpts$ = this._loadingOpts as Observable<LoadingOptions>;

  public user$ = this.userDomainModel.user$;

  public subText$ = this.user$.pipe(
    map(u => {
      switch (u?.challengeName) {
        case UserChallengeName.SMS_MFA:
          return $localize`Enter the six-digit code from the SMS message sent to
          ${u.challengeParameters?.get(UserChallengeParamName.CODE_DELIVERY_DESTINATION)} into the field below.`;
        case UserChallengeName.SOFTWARE_TOKEN_MFA:
          return $localize`Enter the six-digit code from your authentication app into the field below.`;
      }
    })
  );

  public formGroup = new FormGroup({
    timeBasedOneTimePassword: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(6), Validators.maxLength(6)]
    })
  });

  public verifyMfa(req: AuthCodeRequest, portalType: PortalType): void {
    this._loadingOpts.addRequest(this.loadingMessage);
    this.userDomainModel
      .memberEmployerVerifyMFA(
        {
          ...req,
          timeBasedOneTimePassword: NumberUtils.formatNumberWithLeadingZeros(+req.timeBasedOneTimePassword, 6)
        },
        portalType
      )
      .subscribe({
        next: u => {
          if (!u?.challengeName) {
            this._loadingOpts.removeRequest(this.loadingMessage);
            this.router.navigate(['/dashboard'], { replaceUrl: true }).then(() => {});
          }
        },
        error: err => {
          this._loadingOpts.removeRequest(this.loadingMessage);
          this.toastService.publishError(err);
        }
      });
  }
}
